import React, { useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { GlassCard } from '../components/ui/GlassCard';
import { Wind, Flame, BarChart3, Users, Zap, TreePine } from 'lucide-react';

export const WindSynergyAiPage: React.FC = () => {
    const [profile, setProfile] = useState<string | null>(null);

    usePageMetadata({
        title: "WindSynergy AI | Gra Edukacyjna o Zielonej Energii",
        description: "Dołącz do WindSynergy AI! To gamifikacyjne narzędzie do edukacji i promocji zielonej energii. Adoptuj wirtualne turbiny, rywalizuj i ratuj lasy.",
    });

    if (!profile) {
        return (
            <div className="fixed inset-0 flex items-center justify-center text-center p-4 bg-gradient-to-br from-gray-900 via-emerald-900/50 to-gray-900 overflow-hidden">
                <ParticlesBackground variant="minimal" />
                <div className="max-w-md w-full relative z-10">
                    <RevealOnScroll>
                        <div className="flex justify-center gap-2 text-6xl mb-6">
                            <TreePine className="w-16 h-16 text-emerald-400 animate-pulse" />
                            <Zap className="w-16 h-16 text-yellow-400 animate-pulse" style={{ animationDelay: '500ms' }} />
                        </div>
                        <h1 className="text-4xl font-bold text-emerald-400 mb-2">WindSynergy AI</h1>
                        <p className="text-gray-400 mb-8">Wybierz swój profil Eko-Wojownika:</p>
                    </RevealOnScroll>

                    <div className="space-y-4">
                        {[
                            { id: 'activist', icon: <Flame className="w-6 h-6" />, label: 'Rewolucjonista', desc: 'Dla aktywistów i działaczy' },
                            { id: 'dorozala', icon: <BarChart3 className="w-6 h-6" />, label: 'Analityk', desc: 'Dla miłośników danych' },
                            { id: 'ngo', icon: <Users className="w-6 h-6" />, label: 'Organizator', desc: 'Dla liderów społeczności' },
                        ].map((option, index) => (
                            <RevealOnScroll key={option.id} delay={index * 100}>
                                <button
                                    onClick={() => setProfile(option.id)}
                                    className="w-full p-4 rounded-xl bg-white/5 backdrop-blur-sm border-2 border-emerald-500/50 hover:border-emerald-400 hover:bg-emerald-500/10 transition-all duration-300 text-left flex items-center gap-4 group"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
                                        {option.icon}
                                    </div>
                                    <div>
                                        <p className="font-bold text-white">{option.label}</p>
                                        <p className="text-sm text-gray-400">{option.desc}</p>
                                    </div>
                                </button>
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative h-[calc(100vh-64px)] bg-gray-900 text-white">
            <div className="absolute top-4 left-0 right-0 z-[1001]">
                <Breadcrumb />
            </div>

            <MapContainer center={[52.0, 19.0]} zoom={6} className="h-full w-full">
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                    maxZoom={19}
                />
            </MapContainer>

            {/* Dashboard Panel */}
            <div className="absolute top-20 left-4 z-[1000]">
                <GlassCard variant="strong" className="max-w-sm bg-gray-900/90 border-emerald-500/30">
                    <div className="flex items-center gap-2 mb-4">
                        <Wind className="w-6 h-6 text-emerald-400" />
                        <h2 className="font-bold text-lg text-emerald-400">Twoja Misja Ratowania Lasu</h2>
                    </div>
                    <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/10">
                            <span className="flex items-center gap-2">
                                <TreePine className="w-4 h-4 text-emerald-400" />
                                Ocalony las
                            </span>
                            <span className="font-bold text-emerald-400">10 ha</span>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/10">
                            <span className="flex items-center gap-2">
                                <Wind className="w-4 h-4 text-emerald-400" />
                                Zredukowany CO₂
                            </span>
                            <span className="font-bold text-emerald-400">50 t</span>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-500/10">
                            <span className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-yellow-400" />
                                Twoje turbiny
                            </span>
                            <span className="font-bold text-yellow-400">1</span>
                        </div>
                    </div>
                </GlassCard>
            </div>

            {/* Action Button */}
            <div className="absolute top-20 right-4 z-[1000]">
                <button className="px-6 py-3 font-bold rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.02] transition-all duration-300 flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    ADOPTUJ TURBINĘ
                </button>
            </div>
        </div>
    );
};
